import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../msalConfig';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { c } from '../theme';
import athensLogoWhite from '../assets/images/Athens-Logo-White.png';

// ── Background ───────────────────────────────────────────────────────
// The layout is built for a full-bleed photograph. Until marketing supplies
// one, a tuned navy gradient stands in — it reads as deliberate rather than
// as a missing image, and it stays as the fallback behind any photo that is
// slow to load or fails outright.
//
// To use a real photo: drop it in src/assets/images/, uncomment the import,
// and point LOGIN_PHOTO at it. Nothing else needs to change. Aim for a
// landscape shot ~2400px wide with a visually quiet right third, since the
// sign-in column sits over that side.
//
// import loginPhoto from '../assets/images/login-bg.jpg';
const LOGIN_PHOTO = null;

export default function Login() {
  const navigate              = useNavigate();
  const { instance }          = useMsal();
  const { user, reloadAuth }  = useAuth();

  // If AuthContext finishes loading and user is already authenticated
  // (e.g. MSAL navigated back to /login after the redirect), get out of
  // the way and go to the dashboard.
  useEffect(() => {
    if (!user) return;
    const dest = (user.role === 'super_admin' || user.role === 'admin' || user.role === 'finance_reviewer')
      ? '/admin/company'
      : '/review';
    navigate(dest, { replace: true });
  }, [user]);

  const [error,   setError]   = useState('');
  const [loading, setLoading] = useState(false);

  const [devEmail,   setDevEmail]   = useState('');
  const [devRole,    setDevRole]    = useState('super_admin');
  const [devError,   setDevError]   = useState('');
  const [devLoading, setDevLoading] = useState(false);

  // Dev-only SSO bypass — POST /api/auth/dev-login only exists when the
  // backend is run with ENABLE_DEV_LOGIN=true (docker-compose local dev).
  // Goes through the shared api client (VITE_API_BASE_URL), same as every
  // other request — a raw relative fetch() would instead hit Vite's dev
  // proxy, which can't reach the api container under Docker Compose.
  // Storing the token and calling reloadAuth() mirrors exactly what the
  // MSAL exchange path in AuthContext does after POST /api/auth/azure.
  async function handleDevLogin(e) {
    e.preventDefault();
    setDevError('');
    setDevLoading(true);
    try {
      const body = await api.post('/auth/dev-login', { email: devEmail, role: devRole });
      localStorage.setItem('mor_token', body.token);
      localStorage.setItem('mor_user',  JSON.stringify(body.user));
      reloadAuth();
    } catch (err) {
      setDevError(err.message);
    } finally {
      setDevLoading(false);
    }
  }

  // ── Microsoft SSO via redirect ───────────────────────────────
  // loginRedirect navigates the page away to Microsoft. On return, MSAL
  // processes the auth code and AuthContext detects the new account,
  // exchanges the ID token for a local JWT, and loads the user — no work
  // needed here after the redirect call.
  async function handleMicrosoftLogin() {
    setError('');
    setLoading(true);
    try {
      await instance.loginRedirect(loginRequest);
      // Page navigates away — nothing below executes on success.
    } catch (err) {
      setLoading(false);
      setError('Microsoft sign-in failed. Please try again.');
      console.error('[MSAL loginRedirect]', err);
    }
  }

  return (
    <div className="lg-page">
      <style>{css}</style>

      <div
        className="lg-bg"
        style={LOGIN_PHOTO ? { backgroundImage: `url(${LOGIN_PHOTO})` } : undefined}
      />
      <div className="lg-scrim" />

      {/* ── Brand side ───────────────────────────────────────── */}
      <div className="lg-brand">
        <img src={athensLogoWhite} alt="Athens Services" className="lg-logo" />

        <div className="lg-brand-mid">
          <div className="lg-eyebrow">Monthly Operations Review</div>
          <h1 className="lg-headline">
            Operational and financial reporting, in one place.
          </h1>
          <p className="lg-blurb">
            Cost-center dashboards, overtime, fuel, inventory and spend
            analysis for every Athens Services facility.
          </p>
        </div>

        <div className="lg-host">data.athensservices.com</div>
      </div>

      {/* ── Sign-in column ───────────────────────────────────── */}
      <div className="lg-col">
        <div className="lg-col-inner">
          <h2 className="lg-title">Welcome back</h2>
          <p className="lg-sub">
            Use your Athens Services Microsoft account to continue.
          </p>

          <button
            type="button"
            className="lg-btn"
            onClick={handleMicrosoftLogin}
            disabled={loading}
          >
            <MicrosoftLogo />
            {loading ? 'Signing in…' : 'Sign in with Microsoft'}
          </button>

          {error && <div className="lg-error" role="alert">{error}</div>}

          {import.meta.env.DEV && (
            <>
              <div className="lg-rule" />
              <form className="lg-dev-form" onSubmit={handleDevLogin}>
                <div className="lg-dev-label">Dev login — local only, bypasses Microsoft SSO</div>
                <input
                  className="lg-dev-input"
                  type="email"
                  placeholder="dev@local.test"
                  value={devEmail}
                  onChange={(e) => setDevEmail(e.target.value)}
                  required
                />
                <select
                  className="lg-dev-input"
                  value={devRole}
                  onChange={(e) => setDevRole(e.target.value)}
                >
                  <option value="super_admin">super_admin</option>
                  <option value="admin">admin</option>
                  <option value="finance_reviewer">finance_reviewer</option>
                  <option value="reviewer">reviewer</option>
                  <option value="read_only">read_only</option>
                </select>
                <button className="lg-dev-btn" type="submit" disabled={devLoading}>
                  {devLoading ? 'Signing in…' : 'Dev sign in'}
                </button>
                {devError && <div className="lg-error" role="alert">{devError}</div>}
              </form>
            </>
          )}

          <div className="lg-rule" />
          <p className="lg-note">
            Access is restricted to authorized Athens Services personnel.
            Trouble signing in? Contact IT.
          </p>
        </div>
      </div>
    </div>
  );
}

function MicrosoftLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true" focusable="false">
      <rect x="1"  y="1"  width="9" height="9" fill="#F25022" />
      <rect x="11" y="1"  width="9" height="9" fill="#7FBA00" />
      <rect x="1"  y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

// Real CSS rather than inline style objects — this page needs media queries,
// hover and focus states, none of which inline styles can express.
const css = `
.lg-page {
  position: relative;
  min-height: 100vh;
  display: flex;
  overflow: hidden;
  isolation: isolate;
}

/* Background image (or the gradient stand-in) and its readability scrim. */
.lg-bg {
  position: absolute;
  inset: 0;
  z-index: -2;
  background-color: ${c.navyDeep};
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  background-image:
    radial-gradient(52% 44% at 70% 20%, rgba(255,198,122,0.30), transparent 70%),
    radial-gradient(80% 60% at 16% 88%, rgba(16,32,64,0.80), transparent 72%),
    linear-gradient(176deg, #3F4D68 0%, #4A5468 36%, #2A3245 64%, #181F30 100%);
}
.lg-scrim {
  position: absolute;
  inset: 0;
  z-index: -1;
  background: linear-gradient(100deg,
    rgba(17,27,48,0.86) 0%,
    rgba(17,27,48,0.42) 55%,
    rgba(17,27,48,0.32) 100%);
}

/* ── Brand side ─────────────────────────────────────────────── */
.lg-brand {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  padding: 56px 60px;
}
/* align-self is load-bearing: .lg-brand is a column flex container, so the
   default align-items:stretch would blow the wordmark out to full width. */
.lg-logo { height: 30px; width: auto; align-self: flex-start; }
.lg-brand-mid { margin: auto 0; }
.lg-eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #E0555B;
}
.lg-headline {
  font-size: 40px;
  line-height: 1.18;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #fff;
  margin-top: 16px;
  max-width: 15ch;
  text-wrap: balance;
}
.lg-blurb {
  font-size: 15px;
  line-height: 1.65;
  color: rgba(255,255,255,0.72);
  margin-top: 18px;
  max-width: 42ch;
}
.lg-host {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
  color: rgba(255,255,255,0.42);
}

/* ── Frosted sign-in column ─────────────────────────────────── */
.lg-col {
  width: 456px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 52px;
  background: rgba(255,255,255,0.10);
  border-left: 1px solid rgba(255,255,255,0.20);
  -webkit-backdrop-filter: blur(22px) saturate(140%);
  backdrop-filter: blur(22px) saturate(140%);
}
/* Without backdrop-filter the panel would be near-transparent and the text
   unreadable, so fall back to a solid navy column. */
@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
  .lg-col { background: rgba(19,30,54,0.92); }
}
.lg-col-inner { width: 100%; }

.lg-title {
  font-size: 30px;
  font-weight: 650;
  letter-spacing: -0.02em;
  color: #fff;
}
.lg-sub {
  font-size: 14.5px;
  line-height: 1.6;
  color: rgba(255,255,255,0.70);
  margin-top: 10px;
}

.lg-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 11px;
  width: 100%;
  margin-top: 30px;
  padding: 14px;
  border: none;
  border-radius: 9px;
  background: #fff;
  color: ${c.navy};
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 6px 20px rgba(0,0,0,0.28);
  transition: transform 0.12s ease, box-shadow 0.12s ease, background 0.12s ease;
}
.lg-btn:hover:not(:disabled) {
  background: #F4F6F9;
  transform: translateY(-1px);
  box-shadow: 0 10px 26px rgba(0,0,0,0.34);
}
.lg-btn:active:not(:disabled) { transform: translateY(0); }
.lg-btn:disabled { opacity: 0.65; cursor: default; }
.lg-btn:focus-visible { outline: 2px solid #fff; outline-offset: 3px; }

.lg-error {
  margin-top: 16px;
  padding: 10px 13px;
  border-radius: 7px;
  border: 1px solid rgba(255,150,150,0.45);
  background: rgba(190,30,35,0.30);
  color: #FFE1E1;
  font-size: 13px;
}

.lg-rule {
  height: 1px;
  background: rgba(255,255,255,0.18);
  margin: 28px 0 16px;
}
.lg-note {
  font-size: 12.5px;
  line-height: 1.55;
  color: rgba(255,255,255,0.55);
}

/* ── Dev-only SSO bypass form ─────────────────────────────────
   Never shipped — gated by import.meta.env.DEV — so this only ever
   renders against the frosted glass panel in local Docker dev. */
.lg-dev-form { margin-top: 4px; }
.lg-dev-label {
  font-size: 11px;
  color: rgba(255,255,255,0.55);
  margin-bottom: 8px;
}
.lg-dev-input {
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  margin-bottom: 8px;
  border: 1px solid rgba(255,255,255,0.25);
  border-radius: 7px;
  background: rgba(255,255,255,0.08);
  color: #fff;
  font-size: 13px;
  font-family: inherit;
}
.lg-dev-input::placeholder { color: rgba(255,255,255,0.45); }
.lg-dev-btn {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid rgba(255,255,255,0.25);
  border-radius: 7px;
  background: rgba(255,255,255,0.10);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
}
.lg-dev-btn:hover:not(:disabled) { background: rgba(255,255,255,0.18); }
.lg-dev-btn:disabled { opacity: 0.6; cursor: default; }

/* ── Narrow screens ─────────────────────────────────────────── */
/* The column becomes a sheet at the bottom, brand copy above it. */
@media (max-width: 900px) {
  .lg-page { flex-direction: column; }
  .lg-brand { flex: 0 1 auto; padding: 48px 28px 32px; }
  .lg-brand-mid { margin: 26px 0 0; }
  .lg-headline { font-size: 26px; max-width: none; }
  .lg-blurb { font-size: 14px; margin-top: 14px; }
  .lg-host { display: none; }
  .lg-col {
    width: 100%;
    margin-top: auto;
    padding: 30px 28px calc(30px + env(safe-area-inset-bottom));
    border-left: none;
    border-top: 1px solid rgba(255,255,255,0.20);
  }
  .lg-title { font-size: 24px; }
  .lg-sub { font-size: 13.5px; }
  .lg-btn { font-size: 14px; padding: 13px; margin-top: 22px; }
}

@media (max-width: 380px) {
  .lg-brand { padding: 36px 20px 24px; }
  .lg-col { padding: 26px 20px calc(26px + env(safe-area-inset-bottom)); }
}
`;
